import { ProDetailList, ProText } from '@bedrock/eed-react';
import {
  FormCard,
  FormDescriptions,
  FormItemAddressDetail,
  FormItemAdministrativeArea,
  FormItemAttachmentDescription,
  FormItemAttachmentEffectiveDate,
  FormItemAttachmentFile,
  FormItemAttachmentType,
  FormItemBranchInstitutionAddress,
  FormItemBranchInstitutionArea,
  FormItemBranchInstitutionsDetailList,
  FormItemBusinessAddress,
  FormItemBusinessArea,
  FormItemBusinessScope,
  FormItemCreditCode,
  FormItemEnglishName,
  FormItemEstablishTime,
  FormItemFormerName,
  FormItemInvoiceType,
  FormItemOtherAttachmentDetailList,
  FormItemPaidUpCapital,
  FormItemPersonEmail,
  FormItemRegisterCapital,
  FormItemRegisterCountry,
  FormItemTelPhone,
  FormItemVendorAliasName,
  FormItemVendorName,
  FormItemVendorType,
  FormModule,
  FormItemTaxType,
  FormItemInsuredNumberWithFile,
  FormItemLicenseRangeDateWithFile,
  FormItemAttachmentRemark,
} from '@purchase/supplier';
import { Button, Tag } from '@bedrock/components';
import { ChevronDown, ChevronUp } from '@bedrock/icons-react';
import { EllipsisTooltip } from '@mpc/components';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { FC, useMemo, useState } from 'react';
import React from 'react';
import { ProTextWithCopy } from '../../components/ProTextWithCopy';
import { ProLink } from '../../components/ProLink';
import { ProRadioBoolean } from '../../components/ProRadioBoolean';
import DownloadAllButton from './DownloadAllButton';
import './index.less';

const EnterpriseNoFixUnitPrice: FC<{
  isExternal?: boolean;
  isOverseas?: boolean;
  isCompany?: boolean;
  formDetail?: any;
  isDiff?: boolean;
}> = ({
  isExternal = false,
  isOverseas = false,
  isCompany = false,
  formDetail,
  isDiff = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showAllAttachments, setShowAllAttachments] = useState(false);

  const allAttachmentsCount = useMemo(
    () => formDetail?.vendorAttachmentInfo?.otherAttachments?.length ?? 0,
    [formDetail?.vendorAttachmentInfo?.otherAttachments]
  );

  const defaultAttachmentsCount = useMemo(
    () =>
      formDetail?.vendorAttachmentInfo?.defaultOtherAttachments?.length ?? 0,
    [formDetail?.vendorAttachmentInfo?.defaultOtherAttachments]
  );

  // 是否存在需要展开才能看到的附件
  const hasExtraAttachments = allAttachmentsCount > defaultAttachmentsCount;

  // diff 模式下固定使用原始 name，避免收集阶段 name 不匹配
  const attachmentListName: [string, string] = useMemo(() => {
    if (isDiff) return ['vendorAttachmentInfo', 'otherAttachments'];
    return [
      'vendorAttachmentInfo',
      showAllAttachments ? 'otherAttachments' : 'defaultOtherAttachments',
    ];
  }, [isDiff, showAllAttachments]);

  return (
    <FormCard title="基本信息" className="mpc-supplier-new-detail-basic-info">
      <div
        className={classNames({
          'mpc-basic-info-collapsed': !expanded,
        })}
      >
        <FormDescriptions
          columns={expanded ? 2 : 3}
          bordered={expanded ? undefined : false}
          labelStyle={expanded ? { width: 160 } : undefined}
          collapsed={!expanded}
        >
          {/* 展开态独有：供应商名称（排在最前） */}
          {expanded && (
            <FormItemVendorName
              label="供应商名称"
              name={['vendorBasicInfo', 'enterpriseName']}
            />
          )}

          {!isOverseas && (
            <ProTextWithCopy
              label="统一社会信用代码"
              name={['vendorBasicInfo', 'creditCode']}
            />
          )}

          {/* 展开态独有：供应商类型、注册国家/地区 */}
          {expanded && (
            <FormItemVendorType
              label="供应商类型"
              name={['vendorBasicInfo', 'vendorType']}
            />
          )}
          {expanded && (
            <FormItemRegisterCountry
              label="注册国家/地区"
              name={['vendorBasicInfo', 'country']}
            />
          )}

          {!isExternal && (
            <ProTextWithCopy
              label="VMDM供应商编码"
              name={['vendorBasicInfo', 'vendorId']}
            />
          )}
          {!isExternal && (
            <ProTextWithCopy
              label="MPC供应商编码"
              name={['vendorBasicInfo', 'externalVendorId']}
            />
          )}

          {!isOverseas && (
            <ProText
              label="企业类型"
              name={['vendorBasicInfo', 'companyType']}
              render={(value) => <EllipsisTooltip text={value ?? '-'} />}
            />
          )}
          {!isOverseas && (
            <FormItemFormerName
              label="曾用名"
              name={['vendorBasicInfo', 'formerName']}
            />
          )}

          <FormItemVendorAliasName
            label="简称"
            name={['vendorBasicInfo', 'enterpriseAliasName']}
          />
          <FormItemEnglishName
            label="英文全称"
            name={['vendorBasicInfo', 'englishName']}
          />

          {!isOverseas && (
            <FormItemRegisterCapital
              label="注册资本（万）"
              name={['vendorBasicInfo', 'registerCapital']}
            />
          )}
          {!isOverseas && (
            <FormItemPaidUpCapital
              label="实缴资本（万）"
              name={['vendorBasicInfo', 'paidUpCapital']}
            />
          )}

          <FormItemEstablishTime
            label="成立日期"
            name={['vendorBasicInfo', 'establishTime']}
          />

          {/* 展开态独有：税种、参保人数 */}
          {expanded && isOverseas && (
            <FormItemTaxType
              label="税种"
              name={['vendorBasicInfo', 'taxType']}
            />
          )}
          {expanded && !isOverseas && (
            <FormItemInsuredNumberWithFile
              label="参保人数"
              name={['vendorBasicInfo', 'insuredNumberWithFile']}
            />
          )}

          {/* 展开态独有：发票类型、开票税点 */}
          {expanded && (
            <FormItemInvoiceType
              label="发票类型"
              name={['vendorBasicInfo', 'invoiceType']}
            />
          )}
          {expanded && !isOverseas && (
            <ProText
              label="开票税点"
              name={['vendorBasicInfo', 'extendAttributeInfo', 'invoicingTax']}
            />
          )}

          <FormItemLicenseRangeDateWithFile
            label={isCompany ? '营业期限' : '证件有效期'}
            isCompany={isCompany}
            isOverseas={isOverseas}
            name={['vendorBasicInfo', 'licenseRangeDateWithFile']}
          />

          {/* 展开态独有：所属地区、注册地址 */}
          {expanded && (
            <FormItemAdministrativeArea
              label="所属地区"
              name={['vendorBasicInfo', 'administrativeArea']}
            />
          )}
          {expanded && (
            <FormItemAddressDetail
              label="注册地址"
              name={['vendorBasicInfo', 'address']}
            />
          )}

          <FormItemBusinessArea
            label="主要经营地区"
            name={['vendorBasicInfo', 'businessArea']}
          />
          <FormItemBusinessAddress
            label="主要经营地址"
            name={['vendorBasicInfo', 'businessAddress']}
          />

          {!isExternal && !isOverseas && (
            <FormItemPersonEmail
              label="邮箱"
              name={['vendorBasicInfo', 'email']}
            />
          )}
          {!isExternal && !isOverseas && (
            <FormItemTelPhone
              label="电话"
              name={['vendorBasicInfo', 'telPhone']}
            />
          )}

          {!isOverseas && (
            <ProLink
              label="主页网址"
              name={['vendorBasicInfo', 'homeSite']}
              fieldProps={{}}
            />
          )}

          {/* 展开态独有：KOL、单一来源、经营范围、其他经营地 */}
          {expanded && !isExternal && (
            <ProRadioBoolean
              label="是否为KOL合作MCN"
              name={['vendorBasicInfo', 'extendAttributeInfo', 'isKOLForMCN']}
            />
          )}
          {expanded && (
            <ProRadioBoolean
              label="是否单一来源"
              name={[
                'vendorBasicInfo',
                'extendAttributeInfo',
                'isSingleSource',
              ]}
            />
          )}

          {expanded && (
            <FormItemBusinessScope
              label="经营范围"
              name={['vendorBasicInfo', 'businessScope']}
              span={2}
            />
          )}

          {expanded && (
            <FormItemBranchInstitutionsDetailList
              label="其他经营地"
              name={['vendorBasicInfo', 'branchInstitutions']}
              span={2}
            >
              <ProDetailList.Item
                label="#"
                name="#"
                meta={{ columnProps: { width: 50 } }}
              >
                {(value, record, index) => {
                  return index + 1;
                }}
              </ProDetailList.Item>
              <FormItemBranchInstitutionArea
                label="其他经营地区"
                name="manageArea"
                width={180}
                meta={{
                  columnProps: { width: 160, className: 'font-bold' },
                }}
              />
              <FormItemBranchInstitutionAddress
                label="其他经营地址"
                name="manageAddress"
                width={180}
              />
            </FormItemBranchInstitutionsDetailList>
          )}
        </FormDescriptions>
      </div>

      {/* 展开/收起切换按钮 */}
      <div className="mt-2 text-xs text-color-2">
        <Button
          type="link"
          onClick={() => setExpanded(!expanded)}
          suffixIcon={expanded ? <ChevronUp /> : <ChevronDown />}
        >
          {expanded ? '收起' : '展开全部'}
        </Button>
      </div>

      {/* 相关材料 */}
      <FormModule
        title="相关材料"
        titleCount={isDiff ? undefined : allAttachmentsCount}
        proCardProps={{
          expandable: true,
          defaultExpanded: allAttachmentsCount > 0,
          rightSlot: (
            <DownloadAllButton
              formDetail={formDetail}
              isExternal={isExternal}
            />
          ),
        }}
      >
        <FormItemOtherAttachmentDetailList
          name={attachmentListName}
          fieldProps={{ scroll: { x: 1120 } }}
        >
          <FormItemAttachmentType
            label="附件类型"
            name="attachmentType"
            meta={{
              columnProps: { width: 200, className: 'font-bold', fixed: true },
            }}
          />
          <FormItemAttachmentDescription
            label="附件描述"
            name="attachmentDesc"
          />
          <ProDetailList.Item
            label="时效状态"
            name="fileExpiredDateOrLongEffective"
            meta={{ columnProps: { width: 140 } }}
          >
            {(_, record) => {
              const dateInfo = record?.fileExpiredDateOrLongEffective;
              if (
                !dateInfo ||
                (!dateInfo.longEffective && !dateInfo.fileExpiredDate)
              ) {
                return '-';
              }
              if (dateInfo.longEffective) {
                return <Tag color="light-green" text="正常" size="xSmall" />;
              }
              const expireDate = dayjs(dateInfo.fileExpiredDate);
              const today = dayjs();
              const diffDays = expireDate.diff(today, 'day');

              if (diffDays < 0) {
                return <Tag color="light-red" text="已过期" size="xSmall" />;
              }
              if (diffDays <= 60) {
                return (
                  <Tag
                    color="light-orange"
                    text={`${diffDays}天后过期`}
                    size="xSmall"
                  />
                );
              }
              return <Tag color="light-green" text="正常" size="xSmall" />;
            }}
          </ProDetailList.Item>
          <FormItemAttachmentEffectiveDate
            label="文件到期日"
            name="fileExpiredDateOrLongEffective"
            meta={{ columnProps: { width: 140 } }}
          />
          <ProDetailList.Item
            label="附件"
            name="attachmentFiles"
            meta={{ columnProps: { width: 240 } }}
          >
            {(_, record) => (
              <FormItemAttachmentFile
                disableDownload={
                  record?.attachmentType ===
                  FormItemAttachmentType.Type.LegalRepresentativeIdCardCopy
                }
              />
            )}
          </ProDetailList.Item>
          <FormItemAttachmentRemark label="备注" name="remark" />
        </FormItemOtherAttachmentDetailList>

        {/* 展开全部/收起切换按钮 */}
        {hasExtraAttachments && (
          <div className="mt-2 text-xs text-color-2">
            <Button
              type="link"
              onClick={() => setShowAllAttachments((prev) => !prev)}
              suffixIcon={showAllAttachments ? <ChevronUp /> : <ChevronDown />}
            >
              {showAllAttachments ? '收起' : '展开全部'}
            </Button>
          </div>
        )}
      </FormModule>
    </FormCard>
  );
};

export default EnterpriseNoFixUnitPrice;
